/**
 * Wage-related allowances computed from each award's published percentage
 * of its own standard rate (the award's auto-adjustment rule).
 *
 * Artifact:
 * docs/operations/clause-verifications/2026-08-15-aged-care-legislation-wage-related-allowance-percentages.md
 *
 * Standard-rate traps (do not "fix" these to the higher wage column):
 *   - MA000100 cl 3.1 / cl 15.3: SACS L3 pay point 3 **minimum** weekly
 *     ($1,283.10), not the ERO-loaded current weekly ($1,616.71).
 *   - MA000018 cl 3.1 / cl 14.1: Aged care employee—**general**—level 6
 *     ($1,256.00), not the direct-care stream.
 *
 * Expense/CPI allowances (km, meal, uniform, laundry) are not percentages
 * of the standard rate and are not computed here.
 */
import { DEFAULT_TZ, dateOnlyInTz } from '../timezone';
import { roundMoney } from '../money-rounding';
import { SCHADS_ALLOWANCES, SCHADS_BROKEN_SHIFT_FLAT } from '../award-data/schads';

/** AWR 2026 — first full pay period on or after 1 July 2026. */
export const AWR_2026_ALLOWANCE_OPERATIVE_ON = '2026-07-01';

/**
 * SCHADS cl 3.1 standard rate for allowance percentages: the **minimum**
 * weekly wage for a Social and community services employee level 3 at
 * pay point 3 in cl 15.3. Varied PR799380 ppc 1 Jul 2026.
 *
 * Not the ERO-loaded "current weekly wage" at the same row ($1,616.71).
 */
export const SCHADS_ALLOWANCE_STANDARD_WEEKLY_RATE = 1_283.1;

/**
 * Aged Care cl 3.1 standard rate: the minimum wage for an Aged care
 * employee—general—level 6 in cl 14.1. Varied PR799299 ppc 1 Jul 2026.
 *
 * Not the direct-care level 6 weekly wage.
 */
export const AGED_CARE_ALLOWANCE_STANDARD_WEEKLY_RATE = 1_256.0;

/** SCHADS published percentages of the cl 15.3 minimum-column standard rate. */
export const SCHADS_WAGE_RELATED_ALLOWANCE_PERCENT = {
  sleepover: 0.049, // cl 25.7(d)
  onCallWeekday: 0.02, // cl 20.11(a)
  onCallWeekend: 0.0396, // cl 20.11(b)
  firstAidWeekly: 0.0167, // cl 20.6(a)
  brokenShiftOneBreak: 0.017, // cl 20.12(a)
  brokenShiftTwoOrMore: 0.0225, // cl 20.12(b)
} as const;

/** Aged Care published percentages of the cl 14.1 general L6 standard rate. */
export const AGED_CARE_WAGE_RELATED_ALLOWANCE_PERCENT = {
  sleepover: 0.052, // cl 22.9(d)
  leadingHand2to5: 0.0267, // cl 15.3(b)
  leadingHand6to10: 0.0381,
  leadingHand11to15: 0.0481,
  leadingHand16plus: 0.0588,
  nauseousHourly: 0.0005, // cl 15.5(a)
  nauseousWeeklyMin: 0.0027,
} as const;

const FIRST_AID_WEEKLY_DIVISOR = 38;

export const SCHADS_ERO_LOADED_L3_PP3_WEEKLY_RATE = 1_616.71;
export const AGED_CARE_DIRECT_CARE_L6_WEEKLY_RATE_PR791119 = 1_472.0;

/* eslint-disable statura/no-floating-point-money --
 * Award text is "% of the standard rate". FWC publishes the resulting
 * dollars; we compute then pin to those published cents so a wrong
 * standard-rate column cannot silently inflate every allowance.
 */
function percentOfStandardWeekly(percent: number, weekly: number): number {
  return roundMoney(percent * weekly);
}

function dollarsTo4dp(amount: number): number {
  return Math.round(amount * 10_000) / 10_000;
}

function weeklyToHourly4dp(weekly: number, hours: number): number {
  return dollarsTo4dp(weekly / hours);
}
/* eslint-enable statura/no-floating-point-money */

export function isAwr2026AllowanceOperativeOnDate(ymd: string): boolean {
  return ymd >= AWR_2026_ALLOWANCE_OPERATIVE_ON;
}

export function isAwr2026AllowanceOperative(
  shiftStart: Date,
  timezone: string = DEFAULT_TZ,
): boolean {
  return isAwr2026AllowanceOperativeOnDate(dateOnlyInTz(shiftStart, timezone));
}

export type SchadsComputedWageRelatedAllowances = {
  allowances: {
    travelPerKm: number;
    sleepoverRate: number;
    onCallWeekday: number;
    onCallWeekend: number;
    mealAllowance: number;
    firstAidWeekly: number;
    firstAidHourlyPartTime: number;
    uniformPerShift: number;
    uniformWeekly: number;
    laundryPerShift: number;
    laundryWeeklyCap: number;
  };
  brokenShiftFlat: {
    oneBreak: number;
    twoOrMoreBreaks: number;
  };
};

/**
 * AWR 2026 SCHADS wage-related amounts. Expense/CPI fields stay on the
 * Jul 2025 published flats (not a percentage of the standard rate).
 */
export function schadsComputedWageRelatedAllowances(): SchadsComputedWageRelatedAllowances {
  const firstAidWeekly = percentOfStandardWeekly(
    SCHADS_WAGE_RELATED_ALLOWANCE_PERCENT.firstAidWeekly,
    SCHADS_ALLOWANCE_STANDARD_WEEKLY_RATE,
  );
  return {
    allowances: {
      ...SCHADS_ALLOWANCES,
      sleepoverRate: percentOfStandardWeekly(
        SCHADS_WAGE_RELATED_ALLOWANCE_PERCENT.sleepover,
        SCHADS_ALLOWANCE_STANDARD_WEEKLY_RATE,
      ),
      onCallWeekday: percentOfStandardWeekly(
        SCHADS_WAGE_RELATED_ALLOWANCE_PERCENT.onCallWeekday,
        SCHADS_ALLOWANCE_STANDARD_WEEKLY_RATE,
      ),
      onCallWeekend: percentOfStandardWeekly(
        SCHADS_WAGE_RELATED_ALLOWANCE_PERCENT.onCallWeekend,
        SCHADS_ALLOWANCE_STANDARD_WEEKLY_RATE,
      ),
      firstAidWeekly,
      firstAidHourlyPartTime: weeklyToHourly4dp(firstAidWeekly, FIRST_AID_WEEKLY_DIVISOR),
    },
    brokenShiftFlat: {
      oneBreak: percentOfStandardWeekly(
        SCHADS_WAGE_RELATED_ALLOWANCE_PERCENT.brokenShiftOneBreak,
        SCHADS_ALLOWANCE_STANDARD_WEEKLY_RATE,
      ),
      twoOrMoreBreaks: percentOfStandardWeekly(
        SCHADS_WAGE_RELATED_ALLOWANCE_PERCENT.brokenShiftTwoOrMore,
        SCHADS_ALLOWANCE_STANDARD_WEEKLY_RATE,
      ),
    },
  };
}

export type AgedCareComputedWageRelatedAllowances = {
  sleepover: number;
  leadingHand2to5: number;
  leadingHand6to10: number;
  leadingHand11to15: number;
  leadingHand16plus: number;
  nauseousHourly: number;
  nauseousWeeklyMin: number;
};

export function agedCareComputedWageRelatedAllowances(): AgedCareComputedWageRelatedAllowances {
  const weekly = AGED_CARE_ALLOWANCE_STANDARD_WEEKLY_RATE;
  const p = AGED_CARE_WAGE_RELATED_ALLOWANCE_PERCENT;
  return {
    sleepover: percentOfStandardWeekly(p.sleepover, weekly),
    leadingHand2to5: percentOfStandardWeekly(p.leadingHand2to5, weekly),
    leadingHand6to10: percentOfStandardWeekly(p.leadingHand6to10, weekly),
    leadingHand11to15: percentOfStandardWeekly(p.leadingHand11to15, weekly),
    leadingHand16plus: percentOfStandardWeekly(p.leadingHand16plus, weekly),
    nauseousHourly: percentOfStandardWeekly(p.nauseousHourly, weekly),
    nauseousWeeklyMin: percentOfStandardWeekly(p.nauseousWeeklyMin, weekly),
  };
}

/** Jul 2025 published flats — used before AWR 2026 and when a RateContext override is supplied. */
export function schadsJul2025AllowanceVintage(): SchadsComputedWageRelatedAllowances {
  return {
    allowances: { ...SCHADS_ALLOWANCES },
    brokenShiftFlat: { ...SCHADS_BROKEN_SHIFT_FLAT },
  };
}

export function schadsAllowancesForShift(
  shiftStart: Date,
  timezone: string = DEFAULT_TZ,
): SchadsComputedWageRelatedAllowances {
  const vintage = isAwr2026AllowanceOperative(shiftStart, timezone)
    ? schadsComputedWageRelatedAllowances()
    : schadsJul2025AllowanceVintage();
  const date = dateOnlyInTz(shiftStart, timezone);
  // MA000100 cl 20.7(a)/(aa), PR813674. Expense rate, not wage indexation.
  // Evidence: 2026-09-12-vehicle-and-nauseous-weekly-allowances.md.
  vintage.allowances.travelPerKm =
    date >= '2026-09-01' && date < '2027-03-01' ? 1.05 : date >= '2026-07-01' ? 1.01 : 0.99;
  return vintage;
}
